import { useState, useMemo, useEffect } from "react";
import { Building2, Plus, Upload, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddTenantDialog } from "@/components/msp/add-tenant-dialog";
import { BulkImportDialog } from "@/components/msp/bulk-import-dialog";
import {
  alertTotalsById,
  filterTenants,
  sortTenants,
  distinctPlans,
  paginate,
  type TenantSort,
} from "@/lib/msp-list";
import {
  useMspTenants,
  useMspDashboardSummary,
  useMspDashboardHealth,
} from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { TenantOverviewCards } from "@/components/msp/tenant-overview-cards";
import { MspAlertSummary } from "@/components/msp/msp-alert-summary";
import { MspHealthTable } from "@/components/msp/msp-health-table";
import { TenantDrillDown } from "@/components/msp/tenant-drill-down";
import { MspPortfolioReport } from "@/components/msp/msp-portfolio-report";
import { MspBillingSummary } from "@/components/msp/msp-billing-summary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function MSPPortalPage() {
  const [hours, setHours] = useState(24);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [addTenantOpen, setAddTenantOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // #780: client-side search/filter/sort/pagination over the child set.
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [sort, setSort] = useState<TenantSort>("name-asc");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;

  const { user } = useAuth();
  // limit=200 = tier max_children cap, so the full child set arrives in one
  // fetch and all filtering/sorting/paging is done client-side.
  const tenantsQuery = useMspTenants({ limit: 200 });
  const summaryQuery = useMspDashboardSummary(hours);
  const healthQuery = useMspDashboardHealth();

  const tenants = useMemo(() => {
    const raw = tenantsQuery.data?.tenants ?? [];
    const currentTenantId = user?.tenantId;
    if (!currentTenantId) return raw;
    const hasRogue = raw.some((t) => t.parent_msp_id !== currentTenantId);
    if (hasRogue) {
      console.error(
        "[security] MSP tenant list contains tenants with wrong parent_msp_id — discarding response",
      );
      return [];
    }
    return raw;
  }, [tenantsQuery.data, user?.tenantId]);
  const alertSummary = summaryQuery.data?.summary ?? [];
  const integrations = healthQuery.data?.integrations ?? [];

  const planOptions = useMemo(() => distinctPlans(tenants), [tenants]);

  const visibleTenants = useMemo(() => {
    const totals = alertTotalsById(alertSummary);
    const filtered = filterTenants(tenants, {
      search,
      status: statusFilter,
      plan: planFilter,
    });
    return sortTenants(filtered, sort, totals);
  }, [tenants, alertSummary, search, statusFilter, planFilter, sort]);

  // Reset to page 1 whenever the filtered set changes shape.
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, planFilter, sort]);

  const { pageItems, totalPages, safePage } = paginate(
    visibleTenants,
    page,
    PAGE_SIZE,
  );

  const selectedTenant = tenants.find((t) => t.id === selectedTenantId) ?? null;

  const isError =
    tenantsQuery.isError || summaryQuery.isError || healthQuery.isError;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              MSP Portal
            </h1>
            <p className="text-sm text-muted-foreground">
              Multi-tenant overview and management
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setBulkImportOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Bulk import
          </Button>
          <Button onClick={() => setAddTenantOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Client
          </Button>
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            aria-label="Time range"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value={1}>Last 1 hour</option>
            <option value={4}>Last 4 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={72}>Last 3 days</option>
            <option value={168}>Last 7 days</option>
          </select>
        </div>
      </div>

      {isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Failed to load MSP data. Data will retry automatically.
        </div>
      )}

      <MspAlertSummary
        summary={alertSummary}
        isLoading={summaryQuery.isLoading}
      />

      <div>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold">Tenant Overview</h2>
          {tenants.length > 0 && (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients…"
                aria-label="Search clients"
                className="h-9 w-48"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
                className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                aria-label="Filter by plan"
                className="h-9 rounded-md border border-input bg-background px-3 text-sm capitalize ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">All plans</option>
                {planOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as TenantSort)}
                aria-label="Sort clients"
                className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="name-asc">Name (A–Z)</option>
                <option value="name-desc">Name (Z–A)</option>
                <option value="alerts-desc">Most alerts</option>
                <option value="alerts-asc">Fewest alerts</option>
                <option value="status">Status</option>
              </select>
            </div>
          )}
        </div>
        {!tenantsQuery.isLoading &&
        tenants.length > 0 &&
        visibleTenants.length === 0 ? (
          <div className="flex h-32 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
            No clients match your filters
          </div>
        ) : (
          <>
            <TenantOverviewCards
              tenants={pageItems}
              alertSummary={alertSummary}
              isLoading={tenantsQuery.isLoading}
              onSelectTenant={setSelectedTenantId}
            />
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Showing {pageItems.length} of {visibleTenants.length} clients
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(safePage - 1)}
                    disabled={safePage <= 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>
                    Page {safePage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(safePage + 1)}
                    disabled={safePage >= totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Integration Health</CardTitle>
        </CardHeader>
        <CardContent>
          <MspHealthTable
            integrations={integrations}
            isLoading={healthQuery.isLoading}
          />
        </CardContent>
      </Card>

      <MspBillingSummary tenants={tenants} />

      <MspPortfolioReport />

      <TenantDrillDown
        tenant={selectedTenant}
        onClose={() => setSelectedTenantId(null)}
      />

      <AddTenantDialog open={addTenantOpen} onOpenChange={setAddTenantOpen} />
      <BulkImportDialog open={bulkImportOpen} onOpenChange={setBulkImportOpen} />
    </div>
  );
}
